import React from 'react';
import Dropdown from './Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER, TOOLBAR_SEGMENT_ON } from './Toolbar';
import { CONTROL_HEIGHT } from '../../constants/inputs';

/**
 * THE single-choice FILTER CHIP — one compact toolbar control standing in for
 * a row of filter tabs (owner, 2026-10-02 My Tasks header round: the status
 * TAB ROW became one chip; piloted on My Tasks, built here so the order pages
 * and the Breakdown can adopt it later).
 *
 *   at rest:  `All ▾`                     — a GHOST disclosure (`ui/Toolbar`)
 *   open:     a menu of every option with its count, `Blocked (3)`
 *   active:   `● Blocked ×`               — a filled pill; the label reopens
 *                                           the menu, the × returns to All
 *
 * 🔑 IT KNOWS NOTHING ABOUT TASKS OR STATUSES. The caller passes:
 *  - `options` [{ id, label, count? }] IN DISPLAY ORDER, the "all" one included;
 *  - `value`, `onChange(id)`, `allId` (default `'all'`);
 *  - `noun` — what is filtered ("Status"): it reaches assistive technology as
 *    the trigger's name ("Status: All") and the ×'s ("Clear status filter").
 * Counts arrive already computed from the SAME predicate as the rows — the
 * chip never counts (that is how `Done (3)` stays exactly the Done rows).
 *
 * ♿ The active state is never colour alone: the chip NAMES the option and
 * gains a × control; `aria-checked` marks the chosen menu item. The × is a
 * 44px target below `md` (`CONTROL_HEIGHT` + `min-w-[44px]`). Below `md` the
 * menu is `ui/Dropdown`'s bottom sheet, with 44px rows.
 * Session state only — it persists nothing.
 */
const ITEM =
  'w-full flex items-center justify-between gap-3 px-3 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 text-left text-sm rounded-lg transition-colors';
const ITEM_ON = 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-semibold';
const ITEM_OFF = 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700';

/** The ACTIVE chip's two halves sit in one pill (`TOOLBAR_SEGMENT_ON`'s hues).
 *  Each half carries its own focus ring; the pill owns the colour. */
const ACTIVE_PILL = `inline-flex items-stretch rounded-lg ${TOOLBAR_SEGMENT_ON}`;
const ACTIVE_LABEL =
  `inline-flex items-center gap-1.5 ${CONTROL_HEIGHT} pl-3 pr-1 rounded-l-lg text-sm font-semibold ` +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const ACTIVE_CLEAR =
  `inline-flex items-center justify-center ${CONTROL_HEIGHT} min-w-[44px] md:min-w-[2rem] rounded-r-lg ` +
  'hover:bg-blue-200 dark:hover:bg-blue-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

export default function FilterChipMenu({
  options,
  value,
  onChange,
  allId = 'all',
  noun = 'Filter',
  title,
  testId,
}) {
  const current = options.find((o) => o.id === value) || options.find((o) => o.id === allId) || options[0];
  const active = current && current.id !== allId;
  const lowerNoun = noun.toLowerCase();

  const menu = ({ close }) => (
    <div role="menu" aria-label={noun}>
      {options.map((o) => {
        const on = o.id === current.id;
        return (
          <button
            key={o.id}
            type="button"
            role="menuitemradio"
            aria-checked={on}
            onClick={() => { close(); if (!on) onChange(o.id); }}
            className={`${ITEM} ${on ? ITEM_ON : ITEM_OFF}`}
            data-testid={testId ? `${testId}-option-${o.id}` : undefined}
          >
            <span>{o.label}{o.count != null ? ` (${o.count})` : ''}</span>
            {on && <span aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );

  const label = (
    <span className="inline-flex items-center gap-1.5">
      <span className="sr-only">{noun}: </span>
      {active && <span aria-hidden="true" className="text-[0.6rem] leading-none">●</span>}
      <span>{current.label}</span>
    </span>
  );

  if (!active) {
    return (
      <Dropdown
        align="left"
        panelClassName="w-56 p-1"
        title={title || `Filter by ${lowerNoun}`}
        ariaLabel={noun}
        triggerTestId={testId}
        triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
        label={label}
      >
        {menu}
      </Dropdown>
    );
  }

  return (
    <div className={ACTIVE_PILL} data-testid={testId ? `${testId}-active` : undefined}>
      <Dropdown
        align="left"
        panelClassName="w-56 p-1"
        title={title || `Filter by ${lowerNoun}`}
        ariaLabel={noun}
        triggerTestId={testId}
        triggerClassName={ACTIVE_LABEL}
        label={label}
        hideChevron
      >
        {menu}
      </Dropdown>
      <button
        type="button"
        onClick={() => onChange(allId)}
        aria-label={`Clear ${lowerNoun} filter: ${current.label}`}
        title={`Show all — clear the ${lowerNoun} filter`}
        className={ACTIVE_CLEAR}
        data-testid={testId ? `${testId}-clear` : undefined}
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}
